/**
 * ContributionForms.tsx
 * NewVenueForm — saves directly to Supabase with multiple schedules support
 * SuggestEditForm — sends correction suggestions (content only — hours, deals, etc.)
 * (ClaimVenueForm lives in its own file: components/ClaimVenueForm.tsx)
 */

import React, { useState, useRef } from 'react'
import { PlacesSearch, type PlaceResult } from './PlacesSearch'
import { supabase } from '../lib/supabase'
import type { Venue, DayOfWeek } from '../types'
import { DAYS_OF_WEEK } from '../types'
import {
  submitContribution,
  validateEditSuggestion,
  type EditSuggestion,
  type ValidationError,
} from '../services/contributionService'

function Field({ label, required, error, hint, children }: {
  label: string; required?: boolean; error?: string; hint?: string; children: React.ReactNode
}) {
  return (
    <div className="cf-field">
      <label className="cf-label">{label}{required && <span className="cf-required"> *</span>}</label>
      {hint && <p className="cf-hint">{hint}</p>}
      {children}
      {error && <span className="cf-error">{error}</span>}
    </div>
  )
}

function Input({ ...props }: React.InputHTMLAttributes<HTMLInputElement>) {
  return <input className="cf-input" {...props} />
}

function Textarea({ ...props }: React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea className="cf-textarea" {...props} />
}

// ─── PHOTO SCAN ───────────────────────────────

type DealType = 'beer' | 'cocktail' | 'liquor' | 'food' | 'wine' | 'general'
type DealValueType = 'price' | 'amount_off' | 'percent_off'

interface ScannedDeal { type: DealType; description: string; value: string; valueType?: DealValueType }
interface ScannedBlock { days: string[]; startTime: string; endTime: string }
interface ScanResult { deals: ScannedDeal[]; scheduleBlocks?: ScannedBlock[]; dealText?: string }

async function scanMenuPhoto(base64: string, mediaType: string): Promise<ScanResult> {
  const response = await fetch('/api/scan-menu', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ base64, mediaType }),
  })
  const data = await response.json()
  if (data.error) throw new Error(data.error)
  if (data.deals) return data as ScanResult
  const text = data.content?.[0]?.text ?? ''
  if (!text) throw new Error('Empty response')
  const clean = text.replace(/```json|```/g, '').trim()
  return JSON.parse(clean)
}

async function compressImage(file: File): Promise<{ base64: string; mediaType: string }> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onerror = () => reject(new Error('Failed to read file'))
    reader.onload = () => {
      try {
        const result = reader.result as string
        if (!result) return reject(new Error('Empty file result'))
        const img = new Image()
        img.onerror = () => reject(new Error('Failed to load image'))
        img.onload = () => {
          try {
            const MAX_SIZE = 1200
            let { width, height } = img
            if (width > MAX_SIZE || height > MAX_SIZE) {
              if (width > height) { height = Math.round((height * MAX_SIZE) / width); width = MAX_SIZE }
              else { width = Math.round((width * MAX_SIZE) / height); height = MAX_SIZE }
            }
            try {
              const canvas = document.createElement('canvas')
              canvas.width = width; canvas.height = height
              const ctx = canvas.getContext('2d')
              if (!ctx) throw new Error('No canvas context')
              ctx.drawImage(img, 0, 0, width, height)
              const compressed = canvas.toDataURL('image/jpeg', 0.8)
              if (!compressed || compressed === 'data:,') throw new Error('Canvas output empty')
              resolve({ base64: compressed.split(',')[1], mediaType: 'image/jpeg' })
            } catch {
              const mimeType = result.split(';')[0].split(':')[1] || 'image/jpeg'
              resolve({ base64: result.split(',')[1], mediaType: mimeType })
            }
          } catch (err) { reject(err) }
        }
        img.src = result
      } catch (err) { reject(err) }
    }
    reader.readAsDataURL(file)
  })
}

function PhotoScan({ onScanned }: { onScanned: (result: ScanResult) => void }) {
  const [scanning, setScanning] = useState(false)
  const [error, setError] = useState('')
  const [preview, setPreview] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  async function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    setError(''); setScanning(true); setPreview(null)
    try {
      const { base64, mediaType } = await compressImage(file)
      setPreview(`data:${mediaType};base64,${base64}`)
      const result = await scanMenuPhoto(base64, mediaType)
      onScanned(result)
    } catch (err) {
      console.error('Photo scan error:', err)
      setError('Could not read the photo. Try a clearer image or fill in manually.')
    } finally {
      setScanning(false)
      if (inputRef.current) inputRef.current.value = ''
    }
  }

  return (
    <div className="cf-photo-scan">
      <label className="cf-photo-label">
        <input ref={inputRef} type="file" accept="image/*" onChange={handleFile} style={{ display: 'none' }} />
        <div className="cf-photo-btn">
          {scanning ? <><span className="cf-spinner" />Reading your photo...</> : <>📷 Scan happy hour menu or sign</>}
        </div>
      </label>
      {preview && !scanning && (
        <div className="cf-photo-preview">
          <img src={preview} alt="Menu preview" style={{ width: '100%', borderRadius: 8, maxHeight: 160, objectFit: 'cover' }} />
          <div className="cf-photo-success">✓ Deals extracted — review below</div>
        </div>
      )}
      {error && <div className="cf-error">{error}</div>}
    </div>
  )
}

// ─── DEAL TYPE COLORS ───────────────────────────────

const DEAL_COLORS: Record<DealType, { bg: string; color: string; border: string }> = {
  beer:     { bg: '#FFF8E8', color: '#7A5000', border: '#F5D88A' },
  cocktail: { bg: '#EFF6FF', color: '#1E40AF', border: '#BFDBFE' },
  liquor:   { bg: '#FFF0F0', color: '#8B1A1A', border: '#FECACA' },
  food:     { bg: '#F0FDF4', color: '#166534', border: '#BBF7D0' },
  wine:     { bg: '#FFF0F6', color: '#7A0038', border: '#FBCFE8' },
  general:  { bg: '#F5F3EF', color: '#4A4540', border: '#D8D4CC' },
}

const VALUE_TYPE_OPTIONS: { value: DealValueType; label: string }[] = [
  { value: 'price',       label: 'is' },
  { value: 'amount_off',  label: '$ off' },
  { value: 'percent_off', label: '% off' },
]

interface DealItem { type: DealType; description: string; value: string; valueType: DealValueType }

// ─── SCHEDULE BLOCK ───────────────────────────────

interface ScheduleBlock {
  id: number
  days: DayOfWeek[]
  startTime: string
  endTime: string
  isAllDay: boolean
  dealText: string
  deals: DealItem[]
}

function ScheduleEditor({
  block, index, total, onUpdate, onRemove,
}: {
  block: ScheduleBlock
  index: number
  total: number
  onUpdate: (updated: ScheduleBlock) => void
  onRemove: () => void
}) {
  function toggleDay(day: DayOfWeek) {
    onUpdate({ ...block, days: block.days.includes(day) ? block.days.filter(d => d !== day) : [...block.days, day] })
  }
  function addDeal() {
    onUpdate({ ...block, deals: [...block.deals, { type: 'beer', description: '', value: '', valueType: 'price' }] })
  }
  function removeDeal(i: number) {
    onUpdate({ ...block, deals: block.deals.filter((_, idx) => idx !== i) })
  }
  function updateDeal(i: number, field: keyof DealItem, value: string) {
    onUpdate({ ...block, deals: block.deals.map((d, idx) => idx === i ? { ...d, [field]: value } : d) })
  }

  return (
    <div className="cf-schedule-block">
      <div className="cf-schedule-block-header">
        <span className="cf-schedule-block-title">Schedule {index + 1}</span>
        {total > 1 && (
          <button type="button" className="cf-schedule-remove" onClick={onRemove}>✕ Remove</button>
        )}
      </div>

      <Field label="Days" required>
        <div className="cf-days">
          {DAYS_OF_WEEK.map(d => (
            <button key={d} type="button"
              className={`cf-day-btn${block.days.includes(d) ? ' active' : ''}`}
              onClick={() => toggleDay(d)}
            >{d}</button>
          ))}
        </div>
      </Field>

      <div className="cf-checkbox-row" style={{ marginBottom: 10 }}>
        <input type="checkbox" id={`allday-${block.id}`} checked={block.isAllDay}
          onChange={e => onUpdate({ ...block, isAllDay: e.target.checked })} />
        <label htmlFor={`allday-${block.id}`}>All day special</label>
      </div>

      {!block.isAllDay && (
        <div className="cf-row">
          <Field label="Start time">
            <input className="cf-input" type="time" value={block.startTime}
              onChange={e => onUpdate({ ...block, startTime: e.target.value })} />
          </Field>
          <Field label="End time">
            <input className="cf-input" type="time" value={block.endTime}
              onChange={e => onUpdate({ ...block, endTime: e.target.value })} />
          </Field>
        </div>
      )}

      <Field label="Deal items" hint="Add each deal separately">
        {block.deals.map((deal, i) => (
          <div key={i} className="cf-deal-row"
            style={{ borderColor: DEAL_COLORS[deal.type].border, background: DEAL_COLORS[deal.type].bg }}>
            <select className="cf-deal-type" value={deal.type}
              onChange={e => updateDeal(i, 'type', e.target.value)}
              style={{ color: DEAL_COLORS[deal.type].color }}>
              <option value="beer">🍺 Beer</option>
              <option value="cocktail">🍸 Cocktail</option>
              <option value="liquor">🥃 Liquor</option>
              <option value="wine">🍷 Wine</option>
              <option value="food">🍔 Food</option>
              <option value="general">⭐ General</option>
            </select>
            <input className="cf-deal-desc" value={deal.description}
              onChange={e => updateDeal(i, 'description', e.target.value)}
              placeholder="e.g. draft beer" style={{ background: 'transparent' }} />
            {/* Price / $ off / % off — three different deal shapes, not one.
                "$2 Off Drafts" is NOT a $2 draft; forcing it into a single
                "price" field produced nonsense like "$2 Off Drafts $2".
                Labeled "Price" so the "is" dropdown reads as "price is..."
                instead of floating with no context. */}
            <div style={{ width: '100%', marginTop: 4 }}>
              <label className="cf-label" style={{ marginBottom: 4 }}>Price</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <select value={deal.valueType}
                  onChange={e => updateDeal(i, 'valueType', e.target.value)}
                  style={{
                    fontSize: 13, padding: '9px 8px', borderRadius: 8, border: '0.5px solid var(--border-2)',
                    background: '#fff', flexShrink: 0, width: 78,
                  }}>
                  {VALUE_TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                <div style={{ position: 'relative', flex: 1 }}>
                  <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#aaa', fontSize: 13, pointerEvents: 'none' }}>
                    {deal.valueType === 'percent_off' ? '' : '$'}
                  </span>
                  <input className="cf-deal-price" value={deal.value}
                    onChange={e => updateDeal(i, 'value', e.target.value)}
                    placeholder="—" type="number" min="0" step="0.5"
                    style={{
                      background: '#fff', border: '0.5px solid var(--border-2)', borderRadius: 8,
                      padding: '9px 10px', paddingLeft: deal.valueType === 'percent_off' ? 10 : 20,
                      width: '100%', boxSizing: 'border-box' as const, fontSize: 13,
                    }} />
                  {deal.valueType === 'percent_off' && (
                    <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', color: '#aaa', fontSize: 13, pointerEvents: 'none' }}>%</span>
                  )}
                </div>
              </div>
            </div>
            {block.deals.length > 1 && (
              <button type="button" className="cf-deal-remove" onClick={() => removeDeal(i)}>✕</button>
            )}
          </div>
        ))}
        <button type="button" className="cf-add-deal-btn" onClick={addDeal}>+ Add another deal</button>
      </Field>

      <Field label="Deal summary (optional)" hint="Short description shown on the card">
        <Textarea value={block.dealText}
          onChange={e => onUpdate({ ...block, dealText: e.target.value })}
          placeholder="e.g. $3 drafts, $2 off wells, half-off apps" rows={2} />
      </Field>
    </div>
  )
}

// ─── NEW VENUE FORM ───────────────────────────────

let scheduleIdCounter = 1

function makeSchedule(): ScheduleBlock {
  return {
    id: scheduleIdCounter++,
    days: [],
    startTime: '16:00',
    endTime: '19:00',
    isAllDay: false,
    dealText: '',
    deals: [{ type: 'beer', description: '', value: '', valueType: 'price' }],
  }
}

// Builds the human-readable phrase for one deal, matching its actual shape.
// price → "$4 draft beer" | amount_off → "$2 off draft beer" | percent_off → "20% off draft beer"
// This is the fix for cards rendering "$2 Off Drafts $2" — the old code
// always appended "$price" regardless of what the deal actually meant.
function fmtDealPhrase(d: { description: string; value: string; valueType: DealValueType }): string {
  if (!d.value) return d.description
  if (d.valueType === 'amount_off')  return `$${d.value} off ${d.description}`.trim()
  if (d.valueType === 'percent_off') return `${d.value}% off ${d.description}`.trim()
  return `${d.description} $${d.value}`.trim()
}

export function NewVenueForm({ onClose }: { onClose?: () => void }) {
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle')
  const [errorMsg, setErrorMsg] = useState('')

  const [name, setName]               = useState('')
  const [neighborhood, setNeighborhood] = useState('')
  const [city, setCity]               = useState('Cincinnati')   // ← unlocked
  const [address, setAddress]         = useState('')
  const [website, setWebsite]         = useState('')
  const [phone, setPhone]             = useState('')
  const [dogFriendly, setDogFriendly] = useState(false)
  const [categories, setCategories] = useState<string[]>([])
  const [lat, setLat]                 = useState<number | null>(null)
  const [lng, setLng]                 = useState<number | null>(null)
  const [schedules, setSchedules]     = useState<ScheduleBlock[]>([makeSchedule()])
  const [businessHours, setBusinessHours] = useState<any[] | null>(null)

  function handlePlaceSelect(place: PlaceResult) {
    if (place.name)         setName(place.name)
    if (place.address)      setAddress(place.address)
    if (place.phone)        setPhone(place.phone)
    if (place.website)      setWebsite(place.website)
    if (place.neighborhood) setNeighborhood(place.neighborhood)
    if (place.city)         setCity(place.city)       // ← auto-populate city
    if (place.lat)          setLat(place.lat)
    if (place.lng)          setLng(place.lng)
    if (place.business_hours) setBusinessHours(place.business_hours)
  }

  const VALID_DAYS = new Set(['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'])
  const VALID_TIME = /^([01]\d|2[0-3]):[0-5]\d$/
  const VALID_VALUE_TYPES = new Set<DealValueType>(['price', 'amount_off', 'percent_off'])

  function handlePhotoScan(result: ScanResult) {
    const scannedDeals = result.deals?.length ? result.deals.map(d => ({
      type: d.type as DealType,
      description: d.description,
      value: d.value ?? '',
      valueType: VALID_VALUE_TYPES.has(d.valueType as DealValueType) ? (d.valueType as DealValueType) : 'price',
    })) : null

    // Validate every block before trusting it — this is model output, not
    // a typed API response. A bad day name or malformed time would silently
    // corrupt a ScheduleBlock and produce a venue that's wrong in a new way.
    const validBlocks = (result.scheduleBlocks ?? []).filter(b =>
      Array.isArray(b.days) && b.days.length > 0 &&
      b.days.every(d => VALID_DAYS.has(d)) &&
      VALID_TIME.test(b.startTime) && VALID_TIME.test(b.endTime) &&
      b.startTime !== b.endTime
    )

    if (validBlocks.length === 0) {
      // No usable schedule extracted (or the model returned none) — fall
      // back to the old behavior: fill deals into the first block, leave
      // days/times for the submitter to set by hand. Better than losing
      // the deals entirely because the schedule side came back empty.
      setSchedules(prev => prev.map((s, i) => i !== 0 ? s : {
        ...s,
        deals: scannedDeals ?? s.deals,
        dealText: result.dealText || s.dealText,
      }))
      return
    }

    // One ScheduleBlock per distinct time window the photo actually showed —
    // this is the core fix. The old code always wrote into schedule index 0
    // only, so a flyer with 3 windows (Mon–Thu 4–7, Fri 3–7, Sat 3–6)
    // produced exactly one schedule row and silently dropped the other two.
    const newSchedules: ScheduleBlock[] = validBlocks.map(b => ({
      id: scheduleIdCounter++,
      days: b.days as DayOfWeek[],
      startTime: b.startTime,
      endTime: b.endTime,
      isAllDay: false,
      dealText: result.dealText || '',
      deals: scannedDeals ?? [{ type: 'beer', description: '', value: '', valueType: 'price' }],
    }))
    setSchedules(newSchedules)
  }

  function addSchedule() { setSchedules(prev => [...prev, makeSchedule()]) }
  function updateSchedule(index: number, updated: ScheduleBlock) {
    setSchedules(prev => prev.map((s, i) => i === index ? updated : s))
  }
  function removeSchedule(index: number) {
    setSchedules(prev => prev.filter((_, i) => i !== index))
  }

  async function handleSubmit() {
    if (!name.trim())         { setErrorMsg('Venue name is required'); return }
    if (!neighborhood.trim()) { setErrorMsg('Neighborhood is required'); return }
    if (schedules.every(s => s.days.length === 0)) {
      setErrorMsg('Select at least one day on each schedule'); return
    }

    setStatus('submitting')
    setErrorMsg('')

    try {
      let resolvedLat = lat
      let resolvedLng = lng
      if (!resolvedLat && address.trim()) {
        try {
          const geoRes = await fetch(`/api/geocode?address=${encodeURIComponent(address.trim())}`)
          const geoData = await geoRes.json()
          if (geoData.lat) { resolvedLat = geoData.lat; resolvedLng = geoData.lng }
        } catch { /* silently continue without coords */ }
      }

      const { error } = await supabase.from('contributions').insert([{
        flow: 'new_venue',
        status: 'pending',
        data: {
          name: name.trim(),
          neighborhood: neighborhood.trim(),
          city: city.trim() || 'Cincinnati',
          address: address.trim() || '',
          website: website.trim() || '',
          phone: phone.trim() || '',
          latitude: resolvedLat || null,
          longitude: resolvedLng || null,
          dog_friendly: dogFriendly,
          categories,
          business_hours: businessHours,
          schedules: schedules.filter(s => s.days.length > 0).map(s => ({
            days: s.days,
            start_time: s.isAllDay ? '00:00' : s.startTime,
            end_time: s.isAllDay ? '23:59' : s.endTime,
            is_all_day: s.isAllDay,
            deal_text: s.dealText.trim() || s.deals.filter(d => d.description || d.value).map(fmtDealPhrase).join(', '),
            deals: s.deals.filter(d => d.description.trim()).map(d => ({
              type: d.type,
              description: d.description.trim(),
              price: d.valueType === 'price' && d.value ? parseFloat(d.value) : null,
              value: d.value ? parseFloat(d.value) : null,
              value_type: d.valueType,
            })),
          })),
        },
        created_at: new Date().toISOString(),
      }])

      if (error) throw new Error(error.message)

      try {
        await fetch('/api/notify-admin', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            venue_name: name.trim(),
            neighborhood: neighborhood.trim(),
            city: city.trim(),
            submitter_email: '',
            schedules: schedules.filter(s => s.days.length > 0).map(s => ({
              days: s.days,
              start_time: s.isAllDay ? '00:00' : s.startTime,
              end_time: s.isAllDay ? '23:59' : s.endTime,
              deal_text: s.dealText.trim() || s.deals.filter(d => d.description || d.value).map(fmtDealPhrase).join(', '),
            })),
          }),
        })
      } catch { /* never block submission if email fails */ }

      setStatus('success')
    } catch (e: any) {
      setErrorMsg(e.message || 'Something went wrong. Please try again.')
      setStatus('error')
    }
  }

  if (status === 'success') {
    return (
      <div className="cf-success">
        <div className="cf-success-icon">🎉</div>
        <div className="cf-success-title">Spot added!</div>
        <p className="cf-success-msg">Thanks! Your submission is under review and will be published soon.</p>
        <button className="cf-btn-primary" onClick={onClose ?? (() => setStatus('idle'))}>Done</button>
      </div>
    )
  }

  return (
    <div className="cf-form">
      <div className="cf-header">
        <h2 className="cf-title">Add a new spot</h2>
        <p className="cf-subtitle">Submitted for review — published within 24 hours.</p>
      </div>

      <Field label="Find on Google" hint="Type the bar name to auto-fill details">
        <PlacesSearch onSelect={handlePlaceSelect} placeholder="Search for a bar or restaurant..." />
      </Field>

      <div className="cf-divider"><span>Venue details</span></div>

      <Field label="Bar / Restaurant name" required>
        <Input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. The Eagle OTR" />
      </Field>

      <div className="cf-row">
        <Field label="Neighborhood" required>
          <Input value={neighborhood} onChange={e => setNeighborhood(e.target.value)} placeholder="e.g. OTR" />
        </Field>
        <Field label="City">
          {/* City is now editable and auto-populated from Google Places */}
          <Input
            value={city}
            onChange={e => setCity(e.target.value)}
            placeholder="e.g. Cincinnati"
          />
        </Field>
      </div>

      <Field label="Address">
        <Input value={address} onChange={e => setAddress(e.target.value)} placeholder="1342 Vine St, Cincinnati, OH 45202" />
      </Field>

      <div className="cf-row">
        <Field label="Website">
          <Input value={website} onChange={e => setWebsite(e.target.value)} placeholder="https://..." type="url" />
        </Field>
        <Field label="Phone">
          <Input value={phone} onChange={e => setPhone(e.target.value)} placeholder="(513) 555-0100" type="tel" />
        </Field>
      </div>

      <div className="cf-checkbox-row">
        <input type="checkbox" id="dog-friendly" checked={dogFriendly} onChange={e => setDogFriendly(e.target.checked)} />
        <label htmlFor="dog-friendly">🐾 Dog friendly</label>
      </div>

      <Field label="Venue features (optional)" hint="Check any that apply">
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 16px' }}>
          {[
            { key: 'rooftop',     label: '🏙️ Rooftop' },
            { key: 'patio',       label: '☀️ Patio' },
            { key: 'live_music',  label: '🎵 Live music' },
            { key: 'sports_bar',  label: '🏈 Sports bar' },
            { key: 'brewery',     label: '🍺 Brewery' },
            { key: 'waterfront',  label: '🌊 Waterfront' },
          ].map(attr => (
            <div key={attr.key} className="cf-checkbox-row" style={{ marginBottom: 0 }}>
              <input
                type="checkbox"
                id={`cat-${attr.key}`}
                checked={categories.includes(attr.key)}
                onChange={e => setCategories(prev =>
                  e.target.checked ? [...prev, attr.key] : prev.filter(c => c !== attr.key)
                )}
              />
              <label htmlFor={`cat-${attr.key}`}>{attr.label}</label>
            </div>
          ))}
        </div>
      </Field>

      <div className="cf-divider"><span>Scan deals from a photo</span></div>
      <PhotoScan onScanned={handlePhotoScan} />

      <div className="cf-divider"><span>Happy hour schedules</span></div>
      <p className="cf-hint" style={{ marginBottom: 12 }}>Add a separate schedule for each day or group of days with different deals.</p>

      {schedules.map((block, index) => (
        <ScheduleEditor
          key={block.id}
          block={block}
          index={index}
          total={schedules.length}
          onUpdate={updated => updateSchedule(index, updated)}
          onRemove={() => removeSchedule(index)}
        />
      ))}

      <button type="button" className="cf-add-schedule-btn" onClick={addSchedule}>
        + Add another day's schedule
      </button>

      {(status === 'error' || errorMsg) && (
        <div className="cf-error-banner">{errorMsg}</div>
      )}

      <div className="cf-actions">
        {onClose && <button className="cf-btn-secondary" onClick={onClose}>Cancel</button>}
        <button className="cf-btn-primary" onClick={handleSubmit} disabled={status === 'submitting'}>
          {status === 'submitting' ? 'Publishing...' : '🍺 Publish spot'}
        </button>
      </div>
    </div>
  )
}

// ─── SUGGEST EDIT FORM ───────────────────────────────
// CONTENT CORRECTIONS ONLY — hours, deals, prices, etc.
// This form intentionally has NO owner/contact fields. If a submission
// needs owner name/email/phone, it came from components/ClaimVenueForm.tsx,
// not
// here — the two are separate flows with separate admin actions because
// they mean different things: "this fact is wrong" vs "I own this place."

export function SuggestEditForm({ venue, onClose }: { venue: Venue; onClose?: () => void }) {
  const [form, setForm] = useState<Partial<EditSuggestion>>({
    flow: 'suggest_edit', venue_id: venue.id, venue_name: venue.name,
    field_suggestions: '', new_schedule: '', new_deal_details: '', notes: '', submitter_email: '',
  })
  const [errors, setErrors] = useState<ValidationError[]>([])
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle')
  const [resultMessage, setResultMessage] = useState('')

  function fieldError(field: string) { return errors.find(e => e.field === field)?.message }
  function set(field: keyof EditSuggestion, value: string) {
    setForm(f => ({ ...f, [field]: value }))
    setErrors(prev => prev.filter(e => e.field !== field))
  }

  async function handleSubmit() {
    const errs = validateEditSuggestion(form)
    if (errs.length > 0) { setErrors(errs); return }
    setStatus('submitting')
    const result = await submitContribution(form as EditSuggestion)
    setResultMessage(result.message)
    setStatus(result.success ? 'success' : 'error')
  }

  if (status === 'success') {
    return (
      <div className="cf-success">
        <div className="cf-success-icon">✓</div>
        <div className="cf-success-title">Thanks!</div>
        <p className="cf-success-msg">{resultMessage}</p>
        <button className="cf-btn-primary" onClick={onClose ?? (() => setStatus('idle'))}>Done</button>
      </div>
    )
  }

  return (
    <div className="cf-form">
      <div className="cf-header">
        <h2 className="cf-title">Suggest a correction</h2>
        <p className="cf-subtitle">For {venue.name} — hours, deals, or other details that are out of date.</p>
      </div>
      <div className="cf-field">
        <label className="cf-label">What needs to change? <span className="cf-required">*</span></label>
        <textarea className="cf-textarea" value={form.field_suggestions}
          onChange={e => set('field_suggestions', e.target.value)}
          placeholder="e.g. Happy hour ends at 7pm not 6pm, beer specials changed to $4" rows={3} />
        {fieldError('field_suggestions') && <span className="cf-error">{fieldError('field_suggestions')}</span>}
      </div>
      <div className="cf-field">
        <label className="cf-label">Updated schedule (optional)</label>
        <input className="cf-input" value={form.new_schedule} onChange={e => set('new_schedule', e.target.value)} placeholder="e.g. Mon–Fri 3–7pm" />
      </div>
      <div className="cf-field">
        <label className="cf-label">Updated deal details (optional)</label>
        <textarea className="cf-textarea" value={form.new_deal_details} onChange={e => set('new_deal_details', e.target.value)} placeholder="e.g. $4 drafts, $6 cocktails" rows={2} />
      </div>
      <div className="cf-field">
        <label className="cf-label">Your email (optional)</label>
        <input className="cf-input" value={form.submitter_email} onChange={e => set('submitter_email', e.target.value)} placeholder="only used to follow up if needed" type="email" />
      </div>
      {status === 'error' && <div className="cf-error-banner">{resultMessage}</div>}
      <div className="cf-actions">
        {onClose && <button className="cf-btn-secondary" onClick={onClose}>Cancel</button>}
        <button className="cf-btn-primary" onClick={handleSubmit} disabled={status === 'submitting'}>
          {status === 'submitting' ? 'Submitting...' : 'Submit correction'}
        </button>
      </div>
    </div>
  )
}

// ClaimVenueForm intentionally NOT here — it lives in its own file,
// components/ClaimVenueForm.tsx, and is already wired to the "Claim it
// free →" banner in VenueDetailPage.tsx. An earlier version of this file
// briefly duplicated it under this name; that duplicate was unused dead
// code (VenueDetailPage imports from the dedicated file, not this one)
// and has been removed to avoid two same-named, differently-behaved
// components existing in the codebase at once.
